import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { AreaService } from '../area.service';
import { IAreaModel } from '../area.model';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
} from '@fi-shared/fi-items-list/fi-items-list';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { IPreviewImageCellValue } from '@fi-shared/fi-wrapper-table/fi-wrapper-table';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { MatSort } from '@angular/material/sort';
import { FiAreaListWrapperTableComponent } from '@fi-app/area/area-list/area-list-wrapper-table/area-list-wrapper-table.component';

@Component({
    selector: 'fi-area-list',
    templateUrl: './area-list.component.html',
    styleUrls: ['./area-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-area-list' },
    standalone: false,
})
export class AreaListComponent extends FiRemoteStateFullDataSource<IAreaModel> implements OnInit {
    private areaService = inject(AreaService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private statisticService = inject(StatisticService);

    public defaultSortColumnName: string = 'Name';
    public defaultSortDirection: boolean = false;
    public objectType: ObjectTypes = ObjectTypes.Area;
    public itemsListType = FiItemsListType.Areas;

    protected readonly itemsListActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('common.delete'),
            disabled: () => !!this.selectedItems()[0]?.ChildItems?.length,
            onClick: () => {
                this.fiStateService.go(FiStateNames.Area.Delete, { Id: this.selectedItems()[0].Id });
            },
        },
    ];
    protected currentListDisplayType: FiDisplayType;

    protected readonly FiStateNames = FiStateNames;
    protected areaAccessRight: boolean = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.Area.Add,
    );

    @ViewChild(FiAreaListWrapperTableComponent, { static: false })
    areaListWrapperTableComponent: FiAreaListWrapperTableComponent;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.setCurrentListDisplayType();
        this.subscribeOnListDisplayTypeChange();
        this.statisticService.Visit(ModuleType.Core);

    }

    protected navigateToAreaView(areaId: number, $event: MouseEvent): void {
        $event.stopPropagation();
        this.fiStateService.go(FiStateNames.Area.View, { Id: areaId });
    }

    protected getAreaImageCellValue(): IPreviewImageCellValue {
        return {
            image: '',
            imageLarge: '',
            iconClass: 'fi-icon-area-ico',
        };
    }

    protected getMatSortInstance(): MatSort {
        // for this case we should provide matSort from FiAreaListWrapperTableComponent
        return this.areaListWrapperTableComponent.matSort;
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): IFiHttpPromise<IItemsListDataFetchResult<IAreaModel>> {
        const parameter: IItemsListParameterBase = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };

        return this.areaService.getAreasListData(parameter);
    }
}
